// Timezone options shared between the survey campaign wizard's Schedule
// step and Review step. Single source of truth for picker options + pretty
// labels the summary screens render. Matches the shape used by the policy
// module so downstream helpers (timezone-utils) can be reused verbatim.

import { LEGACY_TIME_ZONE_ALIASES, normalizeTimeZone } from '@/lib/time/zone'

export interface TimezoneOption {
  value: string
  label: string
  group: TimezoneGroup
}

export type TimezoneGroup = 'Americas' | 'Europe' | 'Asia & Pacific' | 'Other'

export const TIMEZONES: TimezoneOption[] = [
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (ET)', group: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT)', group: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', group: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', group: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', group: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', group: 'Americas' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)', group: 'Americas' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT)', group: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', group: 'Americas' },
  {
    value: 'America/Argentina/Buenos_Aires',
    label: 'Buenos Aires (ART)',
    group: 'Americas',
  },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', group: 'Americas' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Central European (CET)', group: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Eastern European (EET)', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', group: 'Europe' },
  // Asia & Pacific
  { value: 'Asia/Dubai', label: 'Dubai (GST)', group: 'Asia & Pacific' },
  { value: 'Asia/Kolkata', label: 'India (IST)', group: 'Asia & Pacific' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', group: 'Asia & Pacific' },
  { value: 'Asia/Shanghai', label: 'China (CST)', group: 'Asia & Pacific' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', group: 'Asia & Pacific' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', group: 'Asia & Pacific' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', group: 'Asia & Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', group: 'Asia & Pacific' },
  // Other
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', group: 'Other' },
]

export const TIMEZONE_GROUPS: TimezoneGroup[] = [
  'Americas',
  'Europe',
  'Asia & Pacific',
  'Other',
]

// Browsers disagree on canonical IANA ids (Chromium returns "Asia/Calcutta",
// Node/Linux return "Asia/Kolkata"), and PostgreSQL 17 on Ubuntu 24.04 rejects
// the legacy ones (Feedback Desk digest, 2026-09-02: every IST viewer's strip
// 500'd). This module kept its own alias table, a third copy that drifted (no
// America/Coral_Harbour, the id ICU reports for America/Atikokan). It now
// reads every zone through the web's one normaliser (lib/time/zone.ts), whose
// table a parity spec keeps equal to the API's. The export stays for callers.
export const TIMEZONE_ALIASES: Readonly<Record<string, string>> = LEGACY_TIME_ZONE_ALIASES

export const TIMEZONE_LABELS: Record<string, string> = TIMEZONES.reduce(
  (acc, tz) => {
    acc[tz.value] = tz.label
    return acc
  },
  {} as Record<string, string>,
)

/**
 * The current IANA id for `tz` (legacy ids mapped, casing repaired), so two
 * spellings of one zone compare equal and the picker finds its option. A value
 * that is not a zone comes back as it came: a string in, a string out.
 */
export function normalizeTimezone(tz: string): string {
  return normalizeTimeZone(tz) ?? tz
}

export function prettyTimezone(tz: string): string {
  const normalized = normalizeTimezone(tz)
  return TIMEZONE_LABELS[normalized] ?? normalized
}
